import type {Moment} from 'moment-timezone'
import {mmap} from '@freckle/maybe'
import type {ParamFiltersT} from '@freckle/educator-entities/ts/common/helpers/query-params'
import type {operations} from '@freckle/educator-entities/ts/fancy-api/generated-client'

/**
 * Parameters used to filter a set of generic assignment sessions in the API
 */
export type ParamsT = {
  mathStandardSetId: string
  elaStandardSetId: string | undefined | null
  isRetry?: boolean | null | undefined
  isSelfAssigned?: boolean | null | undefined
  isComplete?: boolean | null | undefined
  isTeacherAssignedOrRetry?: boolean | null | undefined
  timeSpan?:
    | {
        from: Moment
        to: Moment
      }
    | null
    | undefined
  completedAt?: ParamFiltersT<Moment> | null | undefined
  assignmentIds?: Array<number> | null | undefined
  accuracy?: ParamFiltersT<number> | null | undefined
  studentId?: number
}

type Q = operations['getCourseSubjectProductSessionsR']['parameters']['query']

/**
 * Turn a ParamsT into a query string to be tacked onto a generic assignment sessions URL
 */
export function fromParams(params: ParamsT): Q {
  return {
    'math-standard-set-id': params.mathStandardSetId,
    ...mmap(x => ({'ela-standard-set-id': x}) satisfies Partial<Q>, params.elaStandardSetId),
    ...mmap(
      x =>
        x ? {'is-assignment-retry': ''} : ({'is-not-assignment-retry': ''} satisfies Partial<Q>),
      params.isRetry,
    ),
    ...mmap(
      x => (x ? {'self-assigned': ''} : {'teacher-assigned': ''}) satisfies Partial<Q>,
      params.isSelfAssigned,
    ),
    ...mmap(x => (x ? {complete: ''} : {incomplete: ''}) satisfies Partial<Q>, params.isComplete),
    ...((params.isTeacherAssignedOrRetry === true
      ? {'is-teacher-assigned-or-retry': true}
      : {}) satisfies Partial<Q>),
    ...mmap(
      ({from, to}) => ({from: from.valueOf(), to: to.valueOf()}) satisfies Partial<Q>,
      params.timeSpan,
    ),
    ...mmap(x => ({assignmentIds: x}) satisfies Partial<Q>, params.assignmentIds),
    ...mmap(x => ({'student-id': x}) satisfies Partial<Q>, params.studentId),
    ...mmap(completedAtFilterParams, params.completedAt),
    ...mmap(accuracyFilterParams, params.accuracy),
  }
}

function completedAtFilterParams(f: ParamFiltersT<Moment>): Partial<Q> {
  return {
    ...mmap(xs => ({'completed-at[in]': xs.map(x => x.valueOf())}) satisfies Partial<Q>, f.in),
    ...mmap(
      xs => ({'completed-at[notin]': xs.map(x => x.valueOf())}) satisfies Partial<Q>,
      f.notin,
    ),
    ...mmap(x => ({'completed-at[gt]': x.valueOf()}) satisfies Partial<Q>, f.gt),
    ...mmap(x => ({'completed-at[gte]': x.valueOf()}) satisfies Partial<Q>, f.gte),
    ...mmap(x => ({'completed-at[lt]': x.valueOf()}) satisfies Partial<Q>, f.lt),
    ...mmap(x => ({'completed-at[lte]': x.valueOf()}) satisfies Partial<Q>, f.lte),
  }
}

function accuracyFilterParams(f: ParamFiltersT<number>): Partial<Q> {
  return {
    ...mmap(xs => ({'accuracy[in]': xs}) satisfies Partial<Q>, f.in),
    ...mmap(xs => ({'accuracy[notin]': xs}) satisfies Partial<Q>, f.notin),
    ...mmap(x => ({'accuracy[gt]': x}) satisfies Partial<Q>, f.gt),
    ...mmap(x => ({'accuracy[gte]': x}) satisfies Partial<Q>, f.gte),
    ...mmap(x => ({'accuracy[lt]': x}) satisfies Partial<Q>, f.lt),
    ...mmap(x => ({'accuracy[lte]': x}) satisfies Partial<Q>, f.lte),
  }
}
